import {isWidgetFullEditorTarget} from "../../components/common/WidgetFullEditorPortal"
import {RefObject, useCallback, useEffect, useRef, useState} from 'react'
import {type Widget} from '@dv5/domain'
import {CanvasPosition} from '../../model/CanvasPosition'
import {MousePosition} from '../../model/MousePosition'
import {computeRevealScrollPosition, getCanvasScale, getCanvasWheelDelta} from '../utils/canvasViewport'
import {canvasScrollPositionService} from '../../services/canvasScrollPositionService'
import {createThrottler, Throttler} from '../../services/throttle'

const SAVE_SCROLL_POSITION_INTERVAL_MS = 1000
const ZERO_POSITION: CanvasPosition = {left: 0, top: 0}

export interface CanvasRevealTarget {
    widget: Widget
    nonce: number
}

export const useCanvasViewport = (
    canvasId: string,
    zoom: number,
    viewportRef: RefObject<HTMLDivElement | null>,
    savedScrollPosition?: CanvasPosition,
    revealTarget?: CanvasRevealTarget,
) => {
    // Position is derived: user panning/wheel input wins, then the saved
    // position restored on load, then the origin. This makes restoring
    // independent of effect ordering (incl. StrictMode double-invocation).
    const [userScrollPosition, setUserScrollPosition] = useState<CanvasPosition | null>(null)
    const scrollPosition = userScrollPosition ?? savedScrollPosition ?? ZERO_POSITION
    const [isPanning, setIsPanning] = useState(false)
    const panPosition = useRef<MousePosition | null>(null)
    const canvasIdRef = useRef(canvasId)
    const savedScrollPositionRef = useRef(savedScrollPosition)
    savedScrollPositionRef.current = savedScrollPosition
    const throttlerRef = useRef<Throttler<CanvasPosition> | null>(null)
    if (throttlerRef.current === null) {
        throttlerRef.current = createThrottler((position) => {
            canvasScrollPositionService.updateScrollPosition(canvasIdRef.current, position.left, position.top)
        }, SAVE_SCROLL_POSITION_INTERVAL_MS)
    }

    useEffect(() => {
        setUserScrollPosition(null)
        panPosition.current = null
        setIsPanning(false)
        canvasIdRef.current = canvasId
        return () => {
            // Persist the final position of the previous canvas before switching away.
            throttlerRef.current?.flush()
        }
    }, [canvasId])

    // Persist user viewport changes, at most once per second (leading + trailing).
    // Derived/restored positions are never saved back.
    useEffect(() => {
        if (userScrollPosition === null) return
        throttlerRef.current?.schedule(userScrollPosition)
    }, [userScrollPosition])

    // Center the viewport on a requested widget (search hit navigation).
    // The nonce guard keeps a zoom change from re-centering an old request.
    const handledRevealNonceRef = useRef<number | null>(null)
    useEffect(() => {
        if (!revealTarget) return
        if (handledRevealNonceRef.current === revealTarget.nonce) return
        const viewport = viewportRef.current
        if (!viewport) return
        handledRevealNonceRef.current = revealTarget.nonce
        setUserScrollPosition(computeRevealScrollPosition(
            revealTarget.widget,
            {width: viewport.clientWidth, height: viewport.clientHeight},
            zoom,
        ))
    }, [revealTarget, zoom, viewportRef])

    useEffect(() => {
        const viewport = viewportRef.current
        if (!viewport) return

        const changeScrollPosition = (delta: CanvasPosition) => {
            setUserScrollPosition(previous => {
                const base = previous ?? savedScrollPositionRef.current ?? ZERO_POSITION
                return {left: base.left + delta.left, top: base.top + delta.top}
            })
        }
        const stopPan = () => {
            panPosition.current = null
            setIsPanning(false)
        }
        const onWheel = (event: WheelEvent) => {
            if (event.ctrlKey || event.metaKey || isWidgetFullEditorTarget(event.target)) return
            event.preventDefault()
            event.stopPropagation()
            const delta = getCanvasWheelDelta(event, viewport.getBoundingClientRect(), zoom)
            changeScrollPosition(delta)
        }
        const onMouseDown = (event: MouseEvent) => {
            if (event.button !== 1 || isWidgetFullEditorTarget(event.target)) return
            event.preventDefault()
            event.stopPropagation()
            panPosition.current = {x: event.clientX, y: event.clientY}
            setIsPanning(true)
        }
        const onPointerDown = (event: PointerEvent) => {
            // Keep editor pointer handlers from acting on a middle-button press.
            // Do not cancel it here: the following mousedown starts panning.
            if (event.button === 1 && !isWidgetFullEditorTarget(event.target)) event.stopPropagation()
        }
        const onMouseMove = (event: MouseEvent) => {
            const previous = panPosition.current
            if (!previous) return
            event.stopPropagation()
            if (!(event.buttons & 4)) {
                stopPan()
                return
            }
            event.preventDefault()
            const delta = {
                left: (previous.x - event.clientX) / getCanvasScale(zoom),
                top: (previous.y - event.clientY) / getCanvasScale(zoom),
            }
            panPosition.current = {x: event.clientX, y: event.clientY}
            changeScrollPosition(delta)
        }
        const onMouseUp = (event: MouseEvent) => {
            if (event.button !== 1 || !panPosition.current) return
            event.preventDefault()
            event.stopPropagation()
            stopPan()
        }
        const onAuxClick = (event: MouseEvent) => {
            if (event.button !== 1 || isWidgetFullEditorTarget(event.target)) return
            event.preventDefault()
            event.stopPropagation()
        }

        viewport.addEventListener('wheel', onWheel, {capture: true, passive: false})
        viewport.addEventListener('mousedown', onMouseDown, true)
        viewport.addEventListener('pointerdown', onPointerDown, true)
        viewport.addEventListener('auxclick', onAuxClick, true)
        window.addEventListener('mousemove', onMouseMove, true)
        window.addEventListener('mouseup', onMouseUp, true)
        window.addEventListener('blur', stopPan)
        window.addEventListener('pointercancel', stopPan)
        return () => {
            viewport.removeEventListener('wheel', onWheel, true)
            viewport.removeEventListener('mousedown', onMouseDown, true)
            viewport.removeEventListener('pointerdown', onPointerDown, true)
            viewport.removeEventListener('auxclick', onAuxClick, true)
            window.removeEventListener('mousemove', onMouseMove, true)
            window.removeEventListener('mouseup', onMouseUp, true)
            window.removeEventListener('blur', stopPan)
            window.removeEventListener('pointercancel', stopPan)
        }
    }, [zoom, viewportRef])

    const resetScroll = useCallback(() => {
        setUserScrollPosition(ZERO_POSITION)
    }, [])

    const navigateTo = useCallback((position: CanvasPosition) => {
        setUserScrollPosition(position)
    }, [])

    return {scrollPosition, isPanning, resetScroll, navigateTo}
}
